import { reactive, computed, watch, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue'
import { useStore } from 'vuex'
import { useNotification } from '@kyvg/vue3-notification'
import SitesService from '@/services/SitesService'

/**
 * Is the site's address really serving yet?
 *
 * A new Castr address is only an alias in the App Platform spec at first; the
 * edge needs 1–2 minutes (alias → certificate → route) and answers a bare
 * Cloudflare 530 until then — which is what "Preview" opened right after
 * creating a site (Trello oP0vr33o). castr keeps `site.hostReadiness`
 * (`{ host, ready, phase, since, detail, startedAt }`, `ready: null` = never
 * checked, i.e. a site older than the feature → not gated) and re-checks on
 * `GET /sites/{id}/hostnames/status`. This composable shares that state per
 * site, polls it while the address is not ready (5 s, 15 s after two
 * minutes, gives up at fifteen) and tells the UI what to gate.
 */

export const FAST_INTERVAL_MS = 5_000
export const SLOW_INTERVAL_MS = 15_000
export const SLOW_AFTER_MS = 2 * 60_000
export const GIVE_UP_AFTER_MS = 15 * 60_000

/** Disabled-button hint while the address is being set up. */
export const LOCKED_TITLE = 'Available once your site address is ready'

/** The wait, as three steps; `phases` = castr phases that map onto each. */
export const SETUP_STEPS = [
	{ id: 'register', label: 'Registering address', phases: ['registering', 'pending_dns'] },
	{ id: 'certificate', label: 'Issuing certificate', phases: ['pending_ssl'] },
	{ id: 'route', label: 'Almost there', phases: ['active'] },
]

export function setupStepIndex(phase) {
	const i = SETUP_STEPS.findIndex((s) => s.phases.includes(phase))
	return i < 0 ? 0 : i
}

export function setupPhaseLabel(phase) {
	if (phase === 'failed') return 'Could not set up the address'
	return SETUP_STEPS[setupStepIndex(phase)].label
}

const states = new Map()

function stateFor(siteId) {
	if (!states.has(siteId)) {
		states.set(siteId, reactive({
			// last answer of GET /sites/{id}/hostnames/status (null until asked)
			status: null,
			loading: false,
			error: null,
			// the "live" toast is shown once per wait
			announced: false,
			pollStartedAt: null,
			lastCheckedAt: null,
			timedOut: false,
			timer: null,
			// components currently asking for polling (ref-count)
			pollers: 0,
		}))
	}
	return states.get(siteId)
}

const toMs = (value) => {
	const ms = value ? new Date(value).getTime() : NaN
	return Number.isFinite(ms) ? ms : null
}

/**
 * @param {string|(() => string)|{ value: string }} siteId
 * @param {{ poll?: boolean }} [options] `poll` = this component drives the
 *   polling while the address is not ready (the site shell and the list row).
 */
export function useSiteHostReadiness(siteId, { poll = false } = {}) {
	const store = useStore()
	const { notify } = useNotification()
	const id = () => (typeof siteId === 'function' ? siteId() : siteId?.value ?? siteId)
	const state = computed(() => stateFor(id()))

	const site = computed(() => store.getters['Sites/siteById'](id()))
	const stored = computed(() => site.value?.hostReadiness ?? null)

	/** castr's view of the link host: the live answer, else what the site carries. */
	const readiness = computed(() => {
		const live = state.value.status
		const base = stored.value
		if (!live) return base
		// "ready" is sticky on castr; a newer store copy that says so wins.
		if (base?.ready === true && live.ready !== true) return base
		return live
	})

	const host = computed(() => readiness.value?.host || site.value?.canonicalHost || site.value?.primaryHost || '')
	const phase = computed(() => readiness.value?.phase || 'registering')
	/** Not reachable yet: castr says so explicitly. Unknown (null) is not gated. */
	const gated = computed(() => readiness.value?.ready === false)
	const ready = computed(() => !gated.value)
	const failed = computed(() => gated.value && phase.value === 'failed')
	const retryPending = computed(() => readiness.value?.retry?.pending === true)
	const detail = computed(() => readiness.value?.detail || readiness.value?.retry?.error || '')
	const stepIndex = computed(() => setupStepIndex(phase.value))
	const phaseLabel = computed(() => setupPhaseLabel(phase.value))
	const timedOut = computed(() => state.value.timedOut)
	const loading = computed(() => state.value.loading)
	const message = computed(() => `Your site address ${host.value} is being set up. This usually takes 1–2 minutes. You can keep adding content meanwhile.`)

	/** When the wait began: castr's `startedAt` (hostname sync / creation), else our first poll. */
	const startedAtMs = () => toMs(readiness.value?.startedAt) ?? state.value.pollStartedAt ?? Date.now()
	const elapsedMs = () => Date.now() - startedAtMs()

	function announce() {
		const s = state.value
		if (s.announced) return
		s.announced = true
		const text = site.value?.status === 'active'
			? `Your site is live at ${host.value}`
			: `Your site address ${host.value} is ready. Preview is available now.`
		notify({ group: 'success', text })
	}

	function apply(payload) {
		const s = state.value
		const wasGated = gated.value
		s.status = payload
		s.lastCheckedAt = Date.now()
		s.error = null
		// mirror into the store so every other view of the site (list row,
		// overview, preview pane) sees the same answer
		if (site.value && payload) {
			const prev = site.value.hostReadiness || {}
			const next = {
				...prev,
				host: payload.host ?? prev.host ?? null,
				ready: payload.ready,
				phase: payload.phase ?? null,
				since: payload.since ?? null,
				at: payload.at ?? null,
				detail: payload.detail ?? null,
				provider: payload.provider ?? prev.provider ?? null,
				startedAt: payload.startedAt ?? prev.startedAt ?? null,
			}
			const same = ['host', 'ready', 'phase', 'since', 'at', 'detail'].every((k) => prev[k] === next[k])
			if (!same) store.commit('Sites/UPSERT_SITE', { ...site.value, hostReadiness: next })
		}
		if (wasGated && payload?.ready === true) announce()
	}

	/** One GET /sites/{id}/hostnames/status; never throws (the UI shows the last state). */
	async function refresh() {
		const s = state.value
		if (s.loading) return s.status
		s.loading = true
		try {
			const res = await SitesService.getHostnamesStatus(id())
			apply(res)
			return res
		} catch (err) {
			// older castr (404) → nothing to gate on; anything else keeps the last state
			s.error = err
			if (err?.code === 404 && site.value?.hostReadiness?.ready === false) {
				store.commit('Sites/UPSERT_SITE', { ...site.value, hostReadiness: { ...site.value.hostReadiness, ready: null } })
			}
			return null
		} finally {
			s.loading = false
			s.timedOut = gated.value && elapsedMs() > GIVE_UP_AFTER_MS
		}
	}

	const shouldPoll = () => gated.value && !(failed.value && !retryPending.value)

	function schedule() {
		const s = state.value
		clearTimeout(s.timer)
		s.timer = null
		if (!s.pollers || !shouldPoll()) return
		if (elapsedMs() > GIVE_UP_AFTER_MS) {
			s.timedOut = true
			return
		}
		const delay = elapsedMs() > SLOW_AFTER_MS ? SLOW_INTERVAL_MS : FAST_INTERVAL_MS
		s.timer = setTimeout(async () => {
			await refresh()
			schedule()
		}, delay)
	}

	/**
	 * First check once the site is known: unknown (never checked) is resolved
	 * once, not-ready keeps polling; a site without the field comes from an
	 * older castr → nothing to ask. Also runs when the site arrives after the
	 * component mounted (the shell loads it asynchronously).
	 */
	function kick() {
		const s = state.value
		if (!s.pollers || s.loading) return
		const r = readiness.value
		if (!r || r.ready === true || r.provider === 'disabled') return
		if (s.status && Date.now() - (s.lastCheckedAt ?? 0) < FAST_INTERVAL_MS) {
			schedule()
			return
		}
		refresh().then(() => schedule())
	}

	/** Start driving the polling (ref-counted across components). */
	function start() {
		const s = state.value
		s.pollers += 1
		if (s.pollers > 1) return
		s.pollStartedAt = s.pollStartedAt ?? Date.now()
		kick()
	}

	function stop(siteKey = id()) {
		const s = stateFor(siteKey)
		s.pollers = Math.max(0, s.pollers - 1)
		if (!s.pollers) {
			clearTimeout(s.timer)
			s.timer = null
		}
	}

	/** Manual "Refresh": one check now, and the polling clock starts over. */
	async function retry() {
		const s = state.value
		s.timedOut = false
		s.pollStartedAt = Date.now()
		await refresh()
		schedule()
	}

	if (poll && getCurrentInstance()) {
		onMounted(start)
		onBeforeUnmount(() => stop())
		// same component, another site (route param change): move the poller over
		watch(id, (now, before) => {
			if (now === before) return
			if (before) stop(before)
			start()
		})
		// the site (and its stored readiness) may land in the store after mount
		watch(stored, () => { if (!state.value.status) kick() })
		watch(gated, (now, before) => {
			if (now && !before) {
				// a new wait (e.g. the primary address changed): toast again when it ends
				state.value.announced = false
				state.value.timedOut = false
			}
			schedule()
		})
	}

	return {
		state,
		site,
		readiness,
		host,
		phase,
		phaseLabel,
		stepIndex,
		steps: SETUP_STEPS,
		gated,
		ready,
		failed,
		retryPending,
		detail,
		timedOut,
		loading,
		message,
		lockedTitle: LOCKED_TITLE,
		refresh,
		retry,
		start,
		stop,
	}
}
